import { LANGS, ORIGIN, pathFor, type Lang } from './routes';
import { translate } from '../i18n';

export interface Seo {
  title: string;
  description: string;
  canonical: string;
  ogImage: string;
}

export function buildSeo(lang: Lang, mode: string): Seo {
  const titleKey = `seo.title.${mode}`;
  const descKey = `seo.desc.${mode}`;
  const title = translate(lang, titleKey) === titleKey ? translate(lang, 'seo.title') : translate(lang, titleKey);
  const description = translate(lang, descKey) === descKey ? translate(lang, 'seo.desc') : translate(lang, descKey);
  return {
    title,
    description,
    canonical: ORIGIN + pathFor(lang, mode),
    ogImage: `${ORIGIN}/og/${mode}.png`,
  };
}

// --- runtime head sync (SPA): keep <head> correct after client navigation ---

function setMeta(selector: string, attr: string, value: string) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, key, val] = selector.match(/\[(.+?)="(.+?)"\]/) ?? [];
    if (key && val) el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

export function applySeo(lang: Lang, mode: string) {
  const seo = buildSeo(lang, mode);
  document.documentElement.lang = lang;
  document.title = seo.title;

  setMeta('meta[name="description"]', 'content', seo.description);
  setMeta('meta[name="keywords"]', 'content', translate(lang, 'seo.keywords'));
  setMeta('meta[name="robots"]', 'content', 'index,follow');
  setMeta('meta[property="og:title"]', 'content', seo.title);
  setMeta('meta[property="og:description"]', 'content', seo.description);
  setMeta('meta[property="og:type"]', 'content', 'website');
  setMeta('meta[property="og:url"]', 'content', seo.canonical);
  setMeta('meta[property="og:image"]', 'content', seo.ogImage);
  setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
  setMeta('meta[name="twitter:title"]', 'content', seo.title);
  setMeta('meta[name="twitter:description"]', 'content', seo.description);
  setMeta('meta[name="twitter:image"]', 'content', seo.ogImage);

  let canon = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canon) {
    canon = document.createElement('link');
    canon.rel = 'canonical';
    document.head.appendChild(canon);
  }
  canon.href = seo.canonical;

  // rebuild hreflang alternates
  document.head.querySelectorAll('link[data-seo-alt]').forEach((n) => n.remove());
  const alts: Array<[string, Lang]> = LANGS.map((l) => [l, l]);
  for (const [hreflang, l] of alts) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = hreflang;
    link.href = ORIGIN + pathFor(l, mode);
    link.setAttribute('data-seo-alt', '1');
    document.head.appendChild(link);
  }
  const xdef = document.createElement('link');
  xdef.rel = 'alternate';
  xdef.hreflang = 'x-default';
  xdef.href = ORIGIN + pathFor('en', mode);
  xdef.setAttribute('data-seo-alt', '1');
  document.head.appendChild(xdef);
}
